import { useQuery } from "@tanstack/react-query";
import { billingApi, type PlanCatalog } from "../features/billing/billingApi";

/** The venue feature-flag keys (mirrors the backend EntitlementKey set). */
export const ENTITLEMENT_KEYS = [
  "FLOOR_PLAN",
  "WAITLIST",
  "SMS",
  "WHATSAPP",
  "CRM_NOTES_TAGS",
  "MULTI_SITE",
  "MEDIA",
  "CUSTOM_DOMAIN",
] as const;

export type EntitlementKey = (typeof ENTITLEMENT_KEYS)[number];

export interface FeatureMeta {
  /** i18n key for the feature's display name (already localized as `entitlement.feature.*`). */
  labelKey: string;
  /** i18n key for the one-line "what this unlocks" description. */
  descKey: string;
}

/** The single source the disabled-nav badge, the Upgrade page and in-page gates all read. */
export const FEATURE_META: Record<EntitlementKey, FeatureMeta> = Object.fromEntries(
  ENTITLEMENT_KEYS.map((key) => [
    key,
    { labelKey: `entitlement.feature.${key}`, descKey: `entitlement.featureDesc.${key}` },
  ]),
) as Record<EntitlementKey, FeatureMeta>;

/**
 * Reverse-lookup: which plan/add-on name unlocks a given entitlement, from the billing catalog
 * (`Plan.includedFlags` / `AddOn.flag`). A plan match wins over an add-on. Pure, so it's unit-testable.
 */
export function planForFeature(
  catalog: PlanCatalog | undefined,
  feature: string | undefined,
): { planName: string | undefined; isAddOn: boolean } {
  if (!catalog || !feature) return { planName: undefined, isAddOn: false };
  const plan = catalog.plans.find((p) => p.includedFlags.includes(feature));
  if (plan) return { planName: plan.name, isAddOn: false };
  const addOn = catalog.addOns.find((a) => a.flag === feature);
  return { planName: addOn?.name, isAddOn: Boolean(addOn) };
}

/**
 * The name of the plan (or add-on) that unlocks `feature`, derived from the live billing catalog — no
 * backend change. Lets the UI say "Requires the {plan}" instead of a bare "not in your plan". Cached
 * 5 min under `["billing","plans"]`.
 */
export function useEntitlementPlan(feature: string | undefined): {
  planName: string | undefined;
  isAddOn: boolean;
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ["billing", "plans"],
    queryFn: ({ signal }) => billingApi.plans(signal),
    staleTime: 5 * 60_000,
    enabled: Boolean(feature),
  });
  return { ...planForFeature(query.data, feature), isLoading: query.isLoading };
}
